<template>
  <!--  # md:rounded-lg-->
  <div class="h-0 min-h-[768px]">
    <div class="flex h-full flex-col">
      <header class="flex flex-none items-center justify-between border border-gray-200 bg-white px-6 py-4 shadow-sm">
        <div v-if="date">
          <h1 class="text-2xl font-semibold text-gray-900">
            <time :datetime="date" class="sm:hidden">{{ dateFr(date, false) }}</time>
            <time :datetime="date" class="hidden sm:inline">{{ dateFr(date, true) }}</time>
          </h1>
        </div>
        <div class="flex items-center">
          <!-- Groupe unique : navigation, date, vue mensuelle et actualisation -->
          <div class="relative flex items-center divide-x divide-gray-300 rounded-md border border-gray-300 bg-white shadow-sm md:items-stretch">
            <router-link :to="`/global_transaction/day/${format(subDays(new Date(date), 1), 'yyyy-MM-dd')}`"
                         class="flex h-9 w-10 items-center justify-center rounded-l-md text-gray-400 hover:bg-gray-50 hover:text-gray-500 focus:relative">
              <span class="sr-only">Jour précédent</span>
              <ChevronLeftIcon class="h-5 w-5" aria-hidden="true"/>
            </router-link>
            <span class="hidden items-center px-3.5 text-sm font-semibold text-gray-900 md:flex">
              {{ date }}
            </span>
            <router-link :to="`/global_transaction/day/${format(addDays(new Date(date), 1), 'yyyy-MM-dd')}`"
                         class="flex h-9 w-10 items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-gray-500 focus:relative">
              <span class="sr-only">Jour suivant</span>
              <ChevronRightIcon class="h-5 w-5" aria-hidden="true"/>
            </router-link>
            <router-link :to="`/global_transaction/month/${date.substring(0, 7)}`"
                         class="hidden items-center px-3.5 text-sm font-medium text-indigo-600 hover:bg-gray-50 md:flex"
                         title="Vue d'ensemble du mois, une ligne par jour">
              Vue mensuelle
            </router-link>
            <button type="button"
                    @click="refreshData"
                    :disabled="loading"
                    :title="loading ? 'Actualisation…' : 'Actualiser les données'"
                    class="flex h-9 items-center gap-1.5 rounded-r-md px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <svg v-if="loading" class="h-4 w-4 animate-spin text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg v-else class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span class="hidden lg:inline">{{ loading ? 'Actualisation…' : 'Actualiser' }}</span>
            </button>
          </div>
          <Menu as="div" class="relative ml-6 md:hidden">
            <MenuButton
                class="-mx-2 flex items-center rounded-full border border-transparent p-2 text-gray-400 hover:text-gray-500">
              <span class="sr-only">Open menu</span>
              <EllipsisHorizontalIcon class="h-5 w-5" aria-hidden="true"/>
            </MenuButton>

            <transition enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95">
              <MenuItems
                  class="absolute right-0 z-10 mt-3 w-48 origin-top-right divide-y divide-gray-100 overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div class="py-1">
                  <MenuItem v-slot="{ active }">
                    <router-link :to="`/global_transaction/day/${format(new Date(), 'yyyy-MM-dd')}`"
                       :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block px-4 py-2 text-sm']">
                      Aller à aujourd'hui
                    </router-link>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button @click="refreshData"
                       :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'block w-full text-left px-4 py-2 text-sm']">
                      Actualiser
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </header>
      <div class="isolate flex flex-auto overflow-hidden">
        <div ref="container" class="flex flex-auto flex-col overflow-auto">
          <div ref="containerNav"
               class="sticky top-0 z-10 grid flex-none grid-cols-7 bg-white text-xs text-gray-500 shadow ring-1 ring-black ring-opacity-5 md:hidden">
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>W</span>
              <!-- Default: "text-gray-900", Selected: "bg-gray-900 text-white", Today (Not Selected): "text-indigo-600", Today (Selected): "bg-indigo-600 text-white" -->
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">19</span>
            </button>
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>T</span>
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-indigo-600">20</span>
            </button>
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>F</span>
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">21</span>
            </button>
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>S</span>
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-900 text-base font-semibold text-white">22</span>
            </button>
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>S</span>
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">23</span>
            </button>
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>M</span>
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">24</span>
            </button>
            <button type="button" class="flex flex-col items-center pb-1.5 pt-3">
              <span>T</span>
              <span
                  class="mt-3 flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold text-gray-900">25</span>
            </button>
          </div>
          <div class="">
            <!-- État de chargement -->
            <div v-if="loading && !dataLoaded" class="flex justify-center items-center py-12">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span class="ml-3 text-gray-600">Chargement des données...</span>
            </div>

            <!-- État d'erreur -->
            <div v-else-if="error" class="text-center py-12">
              <div class="mx-auto h-12 w-12 text-red-400">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 class="mt-2 text-sm font-medium text-gray-900">{{ error }}</h3>
              <button @click="refreshData" 
                      class="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700">
                Réessayer
              </button>
            </div>

            <!-- Contenu principal : deux colonnes, le détail par plateforme en pleine largeur -->
            <div v-else-if="gridData && gridData.global" class="grid grid-cols-1 items-start gap-4 p-4 xl:grid-cols-2">
              
              <!-- Résumé global -->
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h2 class="text-base font-medium text-gray-900">Chiffre d'affaires</h2>
                </div>
              <table class="min-w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Banque</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WHMCS</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="row in globalRows" :key="row.key" class="hover:bg-gray-50">
                  <td class="px-4 py-2 text-sm font-medium text-gray-900" :title="row.tip">{{ row.label }}</td>
                  <td class="px-4 py-2 text-sm font-semibold text-gray-900">
                    <button type="button"
                            class="underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                            :title="row.tip + ' — côté banque (plateformes de paiement). Cliquer pour le détail des transactions.'"
                            @click="openGlobalDetail(row, 'bank')">
                      {{ formatAmount(gridData.global[row.key].bank) }} €
                    </button>
                  </td>
                  <td class="px-4 py-2 text-sm font-semibold text-gray-900">
                    <button type="button"
                            class="underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                            :title="row.tip + ' — côté WHMCS (encaissements clients). Cliquer pour le détail des transactions.'"
                            @click="openGlobalDetail(row, 'whmcs')">
                      {{ formatAmount(gridData.global[row.key].whmcs) }} €
                    </button>
                  </td>
                </tr>

                </tbody>
              </table>
              </div>

              <!-- TVA du jour -->
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden" v-if="gridData.vat">
                <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h2 class="text-base font-medium text-gray-900">TVA du jour</h2>
                </div>
                <div class="grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
                  <div class="px-4 py-3">
                    <p class="text-xs font-medium uppercase tracking-wider text-gray-500">TVA collectée (ventes)</p>
                    <button type="button"
                            class="mt-1 text-lg font-semibold text-gray-900 underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                            title="Somme des taxes des factures WHMCS payées ce jour. Cliquer pour le détail par facture."
                            @click="openVatDetail('collected')">
                      {{ formatAmount(gridData.vat.collected) }} €
                    </button>
                  </div>
                  <div class="px-4 py-3">
                    <p class="text-xs font-medium uppercase tracking-wider text-gray-500">TVA déductible</p>
                    <button type="button"
                            class="mt-1 text-lg font-semibold text-emerald-600 underline decoration-dotted decoration-emerald-400 underline-offset-2 hover:text-emerald-700"
                            title="TVA des factures fournisseurs datées de ce jour (hors supprimées). Cliquer pour le détail par facture."
                            @click="openVatDetail('deductible')">
                      {{ formatAmount(gridData.vat.deductible) }} €
                    </button>
                  </div>
                  <div class="px-4 py-3">
                    <p class="text-xs font-medium uppercase tracking-wider text-gray-500">TVA à payer</p>
                    <button type="button"
                            class="mt-1 text-lg font-semibold underline decoration-dotted underline-offset-2"
                            :class="Number(gridData.vat.net) >= 0 ? 'text-rose-600 decoration-rose-400 hover:text-rose-700' : 'text-emerald-600 decoration-emerald-400 hover:text-emerald-700'"
                            title="TVA collectée moins TVA déductible. Cliquer pour la composition complète."
                            @click="openVatDetail('net')">
                      {{ formatAmount(gridData.vat.net) }} €
                    </button>
                  </div>
                </div>
              </div>

              <!-- Détail par plateforme -->
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden" v-if="gridData.specific">
                <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h2 class="text-base font-medium text-gray-900">Détail par plateforme</h2>
                </div>
              <table class="min-w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plateforme</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entrant</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sortant</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frais</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retraits</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr class="hover:bg-gray-50" v-for="(item, platform) in gridData.specific" :key="platform">
                  <td class="px-4 py-2 text-sm font-medium text-gray-900">
                    <div class="flex items-center gap-2.5">
                      <img :src="platformMeta(platform).icon" :alt="platformMeta(platform).label"
                           class="h-7 w-7 flex-none object-contain"/>
                      <div>
                        <div>{{ platformMeta(platform).label }}</div>
                        <div v-if="item.balance" class="mt-0.5 text-[11px] font-normal text-gray-500" title="Solde disponible sur la plateforme en fin de journée (affiché quand il change)">
                          Solde : <span class="font-medium text-gray-700">{{ formatAmount(item.balance.bank) }} €</span>
                          <span v-if="item.balance.delta" :class="Number(item.balance.delta) >= 0 ? 'text-emerald-600' : 'text-rose-600'">
                            ({{ Number(item.balance.delta) >= 0 ? '+' : '' }}{{ formatAmount(item.balance.delta) }})
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-2 text-sm text-gray-900">
                    <div>
                      <button type="button" class="underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                              :title="'Paiements ' + platform + ' côté banque. Cliquer pour le détail.'"
                              @click="openPlatformDetail(platform, 'amountin', 'bank')">{{ formatAmount(gridData.specific[platform].amountin.bank) }} €</button>
                      <div class="mt-0.5 text-[11px] font-normal text-gray-500" title="Côté WHMCS (encaissements clients)">
                        WHMCS :
                        <button type="button" class="underline decoration-dotted decoration-gray-300 underline-offset-2 hover:text-indigo-600"
                                :title="'Encaissements ' + platform + ' côté WHMCS. Cliquer pour le détail.'"
                                @click="openPlatformDetail(platform, 'amountin', 'whmcs')">{{ formatAmount(gridData.specific[platform].amountin.whmcs) }} €</button>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-2 text-sm text-gray-900">
                    <div>
                      <button type="button" class="underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                              :title="'Remboursements ' + platform + ' côté banque. Cliquer pour le détail.'"
                              @click="openPlatformDetail(platform, 'amountout', 'bank')">{{ formatAmount(gridData.specific[platform].amountout.bank) }} €</button>
                      <div class="mt-0.5 text-[11px] font-normal text-gray-500" title="Côté WHMCS (encaissements clients)">
                        WHMCS :
                        <button type="button" class="underline decoration-dotted decoration-gray-300 underline-offset-2 hover:text-indigo-600"
                                :title="'Sorties ' + platform + ' côté WHMCS. Cliquer pour le détail.'"
                                @click="openPlatformDetail(platform, 'amountout', 'whmcs')">{{ formatAmount(gridData.specific[platform].amountout.whmcs) }} €</button>
                      </div>
                    </div>
                  </td>
                  <td class="px-4 py-2 text-xs text-gray-900">
                    <button type="button" class="underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                            :title="'Frais ' + platform + ' (frais de service et commissions par transaction). Cliquer pour le détail.'"
                            @click="openPlatformDetail(platform, 'fees', 'bank')">{{ formatAmount(gridData.specific[platform].fees.bank) }} €</button>
                  </td>
                  <td class="px-4 py-2 text-xs text-gray-900">
                    <button v-if="gridData.specific[platform].payout && Number(gridData.specific[platform].payout.bank) !== 0"
                            type="button" class="underline decoration-dotted decoration-gray-400 underline-offset-2 hover:text-indigo-600"
                            :title="'Virements sortants ' + platform + ' vers la banque. Cliquer pour le détail.'"
                            @click="openPlatformDetail(platform, 'payout', 'bank')">{{ formatAmount(gridData.specific[platform].payout.bank) }} €</button>
                    <span v-else class="text-gray-400">—</span>
                  </td>
                </tr>

                </tbody>
              </table>
              </div>

              <!-- Factures fournisseurs (seconde colonne, face au détail par plateforme) -->
              <div class="bg-white border border-gray-200 rounded-lg overflow-hidden" v-if="gridData && gridData.invoices && gridData.invoices.length > 0">
                <div class="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h2 class="text-base font-medium text-gray-900">Factures fournisseurs</h2>
                  <p class="text-xs text-gray-600 mt-1">{{ gridData.invoices.length }} facture(s) — {{ formatAmount(invoicesTotal) }} €</p>
                </div>
                <table class="min-w-full">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fournisseur</th>
                      <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr class="cursor-pointer hover:bg-gray-50" v-for="invoice in gridData.invoices" :key="invoice.id"
                        @click="$router.push('/invoice/' + invoice.id)">
                      <td class="px-4 py-2 text-sm">
                        <div class="flex items-center gap-2.5">
                          <img v-if="invoice.avatar" :src="invoice.avatar" class="h-6 w-6 rounded-full" alt=""/>
                          <span class="font-medium text-gray-900">{{ invoice.supplier || '—' }}</span>
                        </div>
                      </td>
                      <td class="px-4 py-2 text-sm">
                        <span v-if="invoice.status"
                              :class="'inline-flex rounded-full bg-' + (invoice.status_color || 'gray') + '-700 px-2 text-xs font-semibold leading-5 text-white'">
                          {{ invoice.status }}
                        </span>
                        <span v-else class="text-gray-400">—</span>
                      </td>
                      <td class="px-4 py-2 text-right text-sm whitespace-nowrap">
                        <span class="font-medium text-gray-900">{{ formatAmount(invoice.amount) }} €</span>
                        <span v-if="invoice.amount_tax" class="ml-1 text-xs text-gray-500">(TVA {{ formatAmount(invoice.amount_tax) }} €)</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Alertes et problèmes -->
              <div class="bg-white border border-orange-200 rounded-lg overflow-hidden" v-if="gridData && gridData.warn && gridData.warn.length > 0">
                <div class="px-4 py-3 border-b border-orange-200 bg-orange-50">
                  <h2 class="text-base font-medium text-gray-900">Alertes</h2>
                  <p class="text-xs text-gray-600 mt-1">{{ gridData.warn.length }} élément(s) à vérifier</p>
                </div>
              <table class="min-w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                <tr class="hover:bg-orange-50" v-for="item in gridData.warn" :key="item.date">
                  <td class="px-4 py-2 text-sm">
                    <div class="font-medium text-gray-900">{{ capitalizeFirstLetter(item.gateway) }}</div>
                    <div class="text-xs text-gray-500">{{ format(new Date(item.date), 'dd/MM/yyyy')}}</div>
                  </td>
                  <td class="px-4 py-2 text-sm">
                    <div class="text-gray-900">{{ item.description || item.id }}</div>
                    <div class="text-xs text-gray-500">{{ item.type }}</div>
                  </td>
                  <td class="px-4 py-2 text-sm">
                    <div class="font-medium text-gray-900">{{ formatAmount(item.amount) }} €</div>
                    <div v-if="item.fee" class="text-xs text-gray-500">Frais: {{ formatAmount(item.fee) }} €</div>
                  </td>
                </tr>
                </tbody>
              </table>
              </div>
            </div>

          </div>

        </div>
        <div class="hidden w-64 flex-none border-l border-gray-200 bg-white/70 px-4 py-5 md:block" v-if="date"
             :class="[loading ? 'opacity-60' : '']">

          <!-- Navigation du calendrier -->
          <div class="flex items-center justify-between mb-3">
            <router-link :to="`/global_transaction/day/${format(startOfMonth(subMonths(new Date(date), 1)), 'yyyy-MM-dd')}`">
              <button type="button" class="p-1 text-gray-400 hover:text-gray-600">
                <ChevronLeftIcon class="h-4 w-4" aria-hidden="true"/>
              </button>
            </router-link>
            <h3 class="text-sm font-medium text-gray-900">{{ moisEnLettresEtAnnee(new Date(date)) }}</h3>
            <router-link :to="`/global_transaction/day/${format(startOfMonth(addMonths(new Date(date), 1)), 'yyyy-MM-dd')}`">
              <button type="button" class="p-1 text-gray-400 hover:text-gray-600">
                <ChevronRightIcon class="h-4 w-4" aria-hidden="true"/>
              </button>
            </router-link>
          </div>
          <!-- Calendrier -->
          <div class="grid grid-cols-7 text-center text-[10px] text-gray-500 mb-1">
            <div class="py-1">L</div>
            <div class="py-1">M</div>
            <div class="py-1">M</div>
            <div class="py-1">J</div>
            <div class="py-1">V</div>
            <div class="py-1">S</div>
            <div class="py-1">D</div>
          </div>
          <div class="grid grid-cols-7 gap-0.5 text-xs">
            <router-link v-for="(day,) in days" :key="day.date"
                        :to="`/global_transaction/day/${day.date}`"
                        :class="[
                          'py-1.5 text-center rounded hover:bg-gray-100',
                          day.isCurrentMonth ? 'text-gray-900' : 'text-gray-400',
                          day.isSelected ? 'bg-indigo-600 text-white' : '',
                          day.isToday && !day.isSelected ? 'bg-indigo-100 text-indigo-700 font-medium' : ''
                        ]">
              {{ day.date.split('-').pop().replace(/^0/, '') }}
            </router-link>
          </div>

          <!-- Raccourci -->
          <div class="mt-4">
            <router-link :to="`/global_transaction/day/${format(new Date(), 'yyyy-MM-dd')}`"
                        class="block w-full py-2 px-4 text-sm text-center text-gray-700 border border-gray-300 rounded hover:bg-gray-50">
              Aujourd'hui
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- Modale deep-dive : composition d'un montant, transaction par transaction -->
    <div v-if="detailModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-gray-900 bg-opacity-50" @click="detailModal = null"></div>
      <div class="relative flex max-h-[80vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        <div class="flex items-start justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <h3 class="text-base font-semibold text-gray-900">{{ detailModal.title }}</h3>
            <p class="mt-0.5 text-xs text-gray-500">{{ detailModal.subtitle }}</p>
          </div>
          <button type="button" class="ml-4 text-gray-400 hover:text-gray-600" @click="detailModal = null">
            <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="overflow-y-auto">
          <table class="min-w-full">
            <thead class="sticky top-0 bg-gray-50">
              <tr>
                <th v-for="column in modalColumns" :key="column.key"
                    class="px-4 py-2 text-xs font-medium uppercase tracking-wider text-gray-500"
                    :class="column.align === 'right' ? 'text-right' : 'text-left'">{{ column.label }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200">
              <tr v-for="(row, index) in detailModal.rows" :key="index" class="hover:bg-gray-50">
                <td v-for="column in modalColumns" :key="column.key"
                    class="px-4 py-2 text-xs"
                    :class="[
                      column.align === 'right' ? 'whitespace-nowrap text-right font-mono' : 'text-gray-700',
                      column.money && Number(row[column.key]) < 0 ? 'text-rose-600' : ''
                    ]">
                  <!-- Badge coloré (statut, pro Oui/Non) -->
                  <template v-if="column.type === 'badge'">
                    <span v-if="row[column.key]"
                          :class="'inline-flex rounded-full bg-' + (row[column.key].color || 'gray') + '-700 px-2 text-xs font-semibold leading-5 text-white'"
                          :title="row[column.key].title || ''">{{ row[column.key].label }}</span>
                    <span v-else class="text-gray-400">—</span>
                  </template>
                  <!-- Entité avec avatar (fournisseur…) -->
                  <template v-else-if="column.type === 'entity'">
                    <span v-if="row[column.key]" class="inline-flex items-center gap-1.5">
                      <img v-if="row[column.key].avatar" :src="row[column.key].avatar" class="h-5 w-5 rounded-full object-cover" alt=""/>
                      <span class="text-gray-900">{{ row[column.key].label }}</span>
                    </span>
                    <span v-else class="text-gray-400">—</span>
                  </template>
                  <template v-else>
                    {{ column.money ? formatAmount(row[column.key]) + ' €' : (row[column.key] ?? '—') }}
                  </template>
                </td>
              </tr>
              <tr v-if="!detailModal.rows.length">
                <td :colspan="modalColumns.length" class="px-4 py-6 text-center text-xs text-gray-500">Aucune transaction ne compose ce montant.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-4 py-2 text-sm">
          <span class="text-xs text-gray-500">{{ detailModal.rows.length }} transaction(s)</span>
          <span class="font-semibold text-gray-900">Total : {{ formatAmount(detailModal.rows.reduce((sum, row) => sum + Number(row.amount), 0)) }} €</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted} from 'vue'
import {ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, EllipsisHorizontalIcon} from '@heroicons/vue/solid'
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/vue'

// Références pour les éléments DOM si nécessaire
const container = ref(null)
const containerNav = ref(null)

</script>


<script>

import axios from "axios";
import moment from 'moment'
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  subDays,
  format,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths
} from "date-fns";

export default {
  props: ['date'],
  data: () => ({
    days: [],
    gridData: null,
    polling: null,
    monthDates: [],
    endpoint: `/global_transaction/day/`,
    daysOfWeek: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    loading: true,
    error: null,
    dataLoaded: false,
    detailModal: null,
    // Lignes du bloc CA : clé de l'API, libellé et explication (tooltip + modale)
    globalRows: [
      {key: 'amountin', label: 'Total', tip: 'Somme de tous les paiements du jour'},
      {key: 'europe', label: 'Europe (20% TVA)', tip: 'Paiements de clients situés dans l\'UE (hors France) : TVA collectée à 20 %'},
      {key: 'enterprise_europe', label: 'Pro Europe (0% TVA)', tip: 'Clients professionnels UE avec numéro de TVA intracommunautaire : autoliquidation, TVA non collectée (sous-ensemble de la ligne Europe)'},
      {key: 'except_europe', label: 'Hors Europe (0% TVA)', tip: 'Clients hors UE et hors France : export, TVA non collectée'},
    ],
  }),
  mounted() {
    this.generateMonthDates()
    this.fetchTransactionData()
    this.setupAutoRefresh()
  },
  beforeUnmount() {
    clearInterval(this.polling)
  },
  methods: {
    // Date longue en français : « mercredi 13 août 2026 »
    dateFr(date, withWeekday = true) {
      const options = withWeekday
        ? {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}
        : {day: 'numeric', month: 'long', year: 'numeric'};
      const label = new Intl.DateTimeFormat('fr-FR', options).format(new Date(date));
      return label.charAt(0).toUpperCase() + label.slice(1);
    },
    // Identité visuelle des plateformes (logos SVG dans public/payment-icons)
    platformMeta(platform) {
      const meta = {
        paypal: {label: 'PayPal'},
        stripe: {label: 'Stripe'},
        dedipass: {label: 'Dedipass'},
        xmoney: {label: 'xMoney'},
        nowpayments: {label: 'NowPayments'},
        bitpay: {label: 'BitPay'},
        qonto: {label: 'Qonto'},
        coinbase: {label: 'Coinbase'},
      };
      const known = meta[platform];
      return {
        label: known ? known.label : this.capitalizeFirstLetter(platform),
        icon: known ? `/payment-icons/${platform}.svg` : '/payment-icons/qonto.svg',
      };
    },
    // ─── Deep-dive : composition des montants (mêmes règles que l'API) ───
    isEurope(country) {
      return ((this.gridData?.detail?.europe_countries) || []).includes(country);
    },
    scopeMatches(key, country, taxId) {
      if (key === 'amountin') return true;
      if (key === 'europe') return this.isEurope(country);
      if (key === 'enterprise_europe') return this.isEurope(country) && !!taxId;
      if (key === 'except_europe') return !this.isEurope(country) && country !== 'FR';
      return false;
    },
    bankRow(transaction, amount) {
      return {
        date: String(transaction.date).substring(11, 16) || String(transaction.date).substring(0, 10),
        source: this.capitalizeFirstLetter(transaction.gateway) + ' (' + transaction.type + ')',
        label: transaction.description || '—',
        country: transaction.whmcs_country || '—',
        pro: transaction.whmcs_tax_id ? {label: 'Oui', color: 'green', title: transaction.whmcs_tax_id} : null,
        amount: amount,
      };
    },
    whmcsRow(transaction, amount) {
      return {
        date: String(transaction.created_at).substring(11, 16) || String(transaction.created_at).substring(0, 10),
        source: this.capitalizeFirstLetter(transaction.gateway) + ' (WHMCS)',
        label: transaction.description || '—',
        country: transaction.country || '—',
        pro: transaction.tax_id ? {label: 'Oui', color: 'green', title: transaction.tax_id} : null,
        amount: amount,
      };
    },
    openGlobalDetail(row, side) {
      const detail = this.gridData?.detail;
      if (!detail) return;
      let rows;
      if (side === 'bank') {
        rows = (detail.bank || [])
          .filter((t) => t.type === 'payment' && this.scopeMatches(row.key, t.whmcs_country, t.whmcs_tax_id))
          .map((t) => this.bankRow(t, t.amount));
      } else {
        rows = (detail.whmcs || [])
          .filter((t) => this.scopeMatches(row.key, t.country, t.tax_id))
          .map((t) => this.whmcsRow(t, t.amountin));
      }
      this.detailModal = {
        title: row.label + ' — ' + (side === 'bank' ? 'Banque' : 'WHMCS'),
        subtitle: row.tip + (side === 'bank' ? ' (paiements enregistrés côté plateformes)' : ' (encaissements WHMCS)'),
        rows,
      };
    },
    openPlatformDetail(platform, metric, side) {
      const detail = this.gridData?.detail;
      if (!detail) return;
      const titles = {amountin: 'Entrant', amountout: 'Sortant', fees: 'Frais', payout: 'Retraits'};
      let rows = [];
      if (side === 'bank') {
        const bank = (detail.bank || []).filter((t) => t.gateway === platform);
        if (metric === 'amountin') rows = bank.filter((t) => t.type === 'payment').map((t) => this.bankRow(t, t.amount));
        if (metric === 'amountout') rows = bank.filter((t) => t.type === 'refund').map((t) => this.bankRow(t, t.amount));
        if (metric === 'payout') rows = bank.filter((t) => t.type === 'payout').map((t) => this.bankRow(t, t.amount));
        if (metric === 'fees') {
          // Convention unique : les frais sont toujours négatifs (coût)
          rows = bank
            .filter((t) => t.type === 'fee' || Number(t.fee) !== 0)
            .map((t) => this.bankRow(t, t.type === 'fee' ? Number(t.amount) : Number(t.fee)));
        }
      } else {
        const whmcs = (detail.whmcs || []).filter((t) => t.gateway === platform);
        if (metric === 'amountin') rows = whmcs.filter((t) => Number(t.amountin) !== 0).map((t) => this.whmcsRow(t, t.amountin));
        if (metric === 'amountout') rows = whmcs.filter((t) => Number(t.amountout) !== 0).map((t) => this.whmcsRow(t, t.amountout));
      }
      this.detailModal = {
        title: this.capitalizeFirstLetter(platform) + ' — ' + titles[metric] + ' (' + (side === 'bank' ? 'Banque' : 'WHMCS') + ')',
        subtitle: metric === 'fees'
          ? 'Frais de service (montant négatif) et commissions par transaction'
          : 'Transactions ' + platform + ' composant ce montant',
        rows,
      };
    },
    openVatDetail(kind) {
      // Colonnes détaillées : pays, statut pro (badge), statut facture (badge),
      // montant TTC, taux et TVA — partagées entre collectée/déductible/net
      const vatColumns = [
        {key: 'date', label: 'Date'},
        {key: 'source', label: 'Facture', type: 'entity'},
        {key: 'country', label: 'Pays'},
        {key: 'pro', label: 'Pro', type: 'badge'},
        {key: 'status', label: 'Statut', type: 'badge'},
        {key: 'invoice_total', label: 'Montant', align: 'right', money: true},
        {key: 'rate', label: 'Taux', align: 'right'},
        {key: 'amount', label: 'TVA', align: 'right', money: true},
      ];
      const collected = ((this.gridData?.detail?.vat_collected) || []).map((invoice) => ({
        date: String(invoice.datepaid).substring(0, 10) + ' ' + String(invoice.datepaid).substring(11, 16),
        source: {label: 'WHMCS #' + invoice.id},
        country: invoice.country || '—',
        pro: invoice.tax_id
          ? {label: 'Oui', color: 'green', title: invoice.tax_id}
          : {label: 'Non', color: 'gray'},
        status: null,
        invoice_total: invoice.total,
        rate: Number(invoice.taxrate) + ' %',
        amount: invoice.tax,
      }));
      const deductible = ((this.gridData?.invoices) || []).map((invoice) => ({
        date: invoice.date || '',
        source: {label: invoice.supplier || 'Facture fournisseur', avatar: invoice.avatar},
        country: invoice.country || '—',
        pro: null,
        status: invoice.status ? {label: invoice.status, color: invoice.status_color || 'gray'} : null,
        invoice_total: invoice.amount,
        rate: invoice.tax_rate !== null && invoice.tax_rate !== undefined ? Number(invoice.tax_rate) + ' %' : '—',
        amount: kind === 'net' ? -Number(invoice.amount_tax) : invoice.amount_tax,
      }));
      const config = {
        collected: {title: 'TVA collectée (ventes)', subtitle: 'Taxes des factures WHMCS payées ce jour — Pro = client avec n° de TVA intracommunautaire', columns: vatColumns, rows: collected},
        deductible: {title: 'TVA déductible', subtitle: 'TVA des factures fournisseurs datées de ce jour', columns: vatColumns, rows: deductible},
        net: {title: 'TVA à payer', subtitle: 'TVA collectée (positif) moins TVA déductible (négatif) — Pro = client avec n° de TVA intracommunautaire', columns: vatColumns, rows: [...collected, ...deductible]},
      };
      this.detailModal = config[kind];
    },
    moisEnLettresEtAnnee(date) {
      const options = {year: 'numeric', month: 'long'};
      const dateObj = new Date(date);
      const dateFormatee = new Intl.DateTimeFormat('fr-FR', options).format(dateObj);

      const [mois, annee] = dateFormatee.split(' ');
      const moisAvecMajuscule = mois.charAt(0).toUpperCase() + mois.slice(1);
      return moisAvecMajuscule + " " + annee;
    },
    generateMonthDates() {
      const start = startOfMonth(new Date(this.date));
      const end = endOfMonth(new Date(this.date));

      // Find the start and end of the calendar grid
      const startOfWeekDate = startOfWeek(start, {weekStartsOn: 1}); // 1 is Monday
      const endOfWeekDate = endOfWeek(end, {weekStartsOn: 1});

      // Calculate the dates for the calendar grid
      this.monthDates = eachDayOfInterval({
        start: startOfWeekDate,
        end: endOfWeekDate
      });
      const days = []
      this.monthDates.forEach(
          (date) => {
            const isCurrentMonth = date >= start && date <= end;

            const isToday = date.toDateString() === new Date().toDateString()
            const isSelected = format(date, 'yyyy-MM-dd') === format(new Date(this.date), 'yyyy-MM-dd')

            days.push({
              "date": format(date, 'yyyy-MM-dd'),
              "isCurrentMonth": isCurrentMonth,
              "isToday": isToday,
              "isSelected": isSelected
            })
          }
      )
      this.days = days
    },
    capitalizeFirstLetter(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0';
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },
    refreshData() {
      this.fetchTransactionData(true);
    },
    setupAutoRefresh() {
      // Rafraîchissement automatique toutes les 5 minutes
      this.polling = setInterval(() => {
        this.fetchTransactionData(false);
      }, 300000);
    },
    async fetchTransactionData(showLoading = true) {
      if (showLoading) {
        this.loading = true;
      }
      this.error = null;
      
      try {
        const token = await this.$auth.getAccessTokenSilently();
        const HTTP = axios.create({
          baseURL: `https://billing.clafoutis.eu/api`,
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const response = await HTTP.get(this.endpoint + this.date);
        this.gridData = response.data;
        this.dataLoaded = true;
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        this.error = 'Erreur lors du chargement des données. Veuillez réessayer.';
        if (error.response && error.response.status === 404) {
          this.error = 'Aucune donnée disponible pour cette date.';
        }
      } finally {
        this.loading = false;
      }
    },
    truncate(v) {
      const newline = v.indexOf('\n')
      return newline > 0 ? v.slice(0, newline) : v
    },
    formatDate(v) {
      return v.replace(/T|Z/g, ' ')
    }
  },
  computed: {
    // Colonnes de la modale deep-dive : jeu spécifique (TVA…) ou jeu standard
    modalColumns() {
      return (this.detailModal && this.detailModal.columns) || [
        {key: 'date', label: 'Date'},
        {key: 'source', label: 'Source'},
        {key: 'label', label: 'Détail'},
        {key: 'country', label: 'Pays'},
        {key: 'pro', label: 'Pro', type: 'badge'},
        {key: 'amount', label: 'Montant', align: 'right', money: true},
      ];
    },
    invoicesTotal() {
      return (this.gridData?.invoices || []).reduce((sum, invoice) => sum + (Number(invoice.amount) || 0), 0);
    },
    weeks() {
      // Split the monthDates into weeks
      const weeks = [];
      for (let i = 0; i < this.monthDates.length; i += 7) {
        weeks.push(this.monthDates.slice(i, i + 7));
      }
      return weeks;
    },
  },
      watch: {
    '$route': function (to, from) {
      this.fetchTransactionData(true);
    },
    date: function (newVal, oldVal) {
      this.generateMonthDates();
      this.fetchTransactionData(true);
    }
  },
}
</script>
